import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { getPublishedPosts } from '../data/blogPosts';
import './LandingPage.css';
import './BlogPage.css';

const SITE_URL = 'https://letscoparent.us';
const BLOG_TITLE = 'Coparenting Blog — practical guides for two-home families | coparent';
const BLOG_DESCRIPTION = 'Honest, practical coparenting advice: custody schedules, shared expenses, calendar logistics, and routines that keep kids first. Updated twice weekly.';

const SharedRootsLogo = ({ width = 28, height = 30 }) => {
  return (
    <svg width={width} height={height} viewBox="0 0 160 160" fill="none">
      <path d="M80 140 L80 95" stroke="#1B6B4A" strokeWidth="7" strokeLinecap="round"/>
      <path d="M80 95 Q65 80, 50 65" stroke="#1B6B4A" strokeWidth="6" strokeLinecap="round" fill="none"/>
      <path d="M80 95 Q95 80, 110 65" stroke="#1B6B4A" strokeWidth="6" strokeLinecap="round" fill="none"/>
      <circle cx="44" cy="50" r="25" fill="#F5C842" opacity="0.85"/>
      <circle cx="116" cy="50" r="25" fill="#F2886D" opacity="0.85"/>
      <circle cx="80" cy="92" r="10" fill="#3BA676"/>
    </svg>
  );
};

const BlogPage = () => {
  const navigate = useNavigate();
  const [navScrolled, setNavScrolled] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState('All');

  const allPosts = useMemo(
    () => getPublishedPosts().slice().sort((a, b) => new Date(b.date) - new Date(a.date)),
    []
  );
  const categories = useMemo(
    () => ['All', ...new Set(allPosts.map((p) => p.category))],
    [allPosts]
  );
  const visiblePosts = activeCategory === 'All'
    ? allPosts
    : allPosts.filter((p) => p.category === activeCategory);

  useEffect(() => {
    const handleScroll = () => {
      setNavScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const itemListSchema = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: allPosts.map((post, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      url: `${SITE_URL}/blog/${post.slug}`,
      name: post.title,
    })),
  };

  return (
    <div className="landing-page blog-page">
      <Helmet>
        <title>{BLOG_TITLE}</title>
        <meta name="description" content={BLOG_DESCRIPTION} />
        <link rel="canonical" href={`${SITE_URL}/blog`} />
        <meta property="og:title" content={BLOG_TITLE} />
        <meta property="og:description" content={BLOG_DESCRIPTION} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${SITE_URL}/blog`} />
        <meta property="og:site_name" content="coparent" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={BLOG_TITLE} />
        <meta name="twitter:description" content={BLOG_DESCRIPTION} />
        <script type="application/ld+json">{JSON.stringify(itemListSchema)}</script>
      </Helmet>
      <nav className={`lp-nav${navScrolled ? ' scrolled' : ''}`}>
        <div className="lp-nav-inner">
          <div className="lp-nav-logo" onClick={() => navigate('/')}>
            <SharedRootsLogo width={38} height={40} />
            <span className="lp-nav-wordmark">coparent</span>
          </div>
          <ul className={`lp-nav-links${mobileNavOpen ? ' mobile-open' : ''}`}>
            <li><button className="lp-nav-link-btn" onClick={() => navigate('/')}>Home</button></li>
            <li><button className="lp-nav-link-btn" onClick={() => { setMobileNavOpen(false); navigate('/blog'); }}>Blog</button></li>
            <li><button className="lp-nav-login" onClick={() => navigate('/login')}>Log In</button></li>
            <li><button className="lp-nav-cta" onClick={() => navigate('/register')}>Get Started</button></li>
          </ul>
          <button className="lp-nav-mobile-toggle" onClick={() => setMobileNavOpen(!mobileNavOpen)} aria-label="Toggle menu">
            <span></span>
            <span></span>
            <span></span>
          </button>
        </div>
      </nav>

      <div className="blog-hero">
        <h1>The coparent blog</h1>
        <p>Practical guidance for families navigating co-parenting together.</p>
      </div>

      <div className="blog-filters">
        {categories.map((cat) => (
          <button
            key={cat}
            type="button"
            onClick={() => setActiveCategory(cat)}
            className={activeCategory === cat ? 'chip-active' : 'chip-inactive'}
          >
            {cat}
          </button>
        ))}
      </div>

      <div className="blog-grid">
        {visiblePosts.map((post) => (
          <Link to={`/blog/${post.slug}`} className="blog-card" key={post.slug}>
            <div className="blog-card-accent" />
            <div className="blog-card-body">
              <span className="blog-card-category">{post.category}</span>
              <h2>{post.title}</h2>
              <p>{post.excerpt}</p>
              <div className="blog-card-meta">
                <span>{post.date}</span>
                <span className="dot">&middot;</span>
                <span>{post.readTime}</span>
              </div>
              <span className="blog-card-read">
                Read article
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M5 12h14" /><path d="m12 5 7 7-7 7" /></svg>
              </span>
            </div>
          </Link>
        ))}
      </div>

      <footer className="blog-footer">
        <Link to="/" className="blog-footer-logo">coparent</Link>
        <p>Parenting in sync.</p>
      </footer>
    </div>
  );
};

export default BlogPage;
